<template>
  <div>
    <div
      class="g-recaptcha"
      data-size="invisible"
      :data-sitekey="googleRecaptchaApiKey"
      data-callback="submitLoginForm"
    />
    <p class="text-h5 font-weight-bold">Welcome to the New York State DMV Road Test Results Website</p>
    <p>To access your results, please enter your:
    <ul>
      <li>9-digit New York State DMV ID number <ExternalLink
                  text="(See a sample)"
                  url="https://dmv.ny.gov/id-card/sample-photo-documents"
                /></li>
      <li>Date of birth</li>
    </ul>
    </p>
    <v-form ref="form" v-model="valid">
      <v-card class="pb-2" elevation="2">
        <v-overlay :value="loading" absolute>
          <v-row>
            <v-col class="text-center">
              <v-img
                contain
                src="@/assets/padlock.gif"
                max-height="50"
                transition="scale-transition"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col class="text-center"> Authenticating, please wait... </v-col>
          </v-row>
        </v-overlay>
        <v-card-title class="justify-center">LOGIN</v-card-title>
        <v-card-text class="centerContainer">
          <v-row>
            <v-text-field
              v-model="CID"
              v-mask="'#########'"
              filled
              counter="9"
              maxlength="9"
              label="DMV ID Number"
              prepend-inner-icon="mdi-card-account-details"
              placeholder="#########"
              :rules="[isValidCIDNumber, isValidCID]"
            />
          </v-row>
          <v-row>
            <v-text-field
              v-model="dateOfBirth"
              v-mask="'##/##/####'"
              class="mt-2"
              filled
              counter="10"
              maxlength="10"
              label="Date of Birth"
              prepend-inner-icon="mdi-calendar-month"
              placeholder="mm/dd/yyyy"
              :rules="birthDateRules"
            />
          </v-row>
        </v-card-text>
        <v-card-actions class="centerContainer">
          <v-btn
            class="justify-center loginButton"
            color="primary"
            :disabled="!valid"
            height="45px"
            block
            @click="login()"
          >
            <v-icon>mdi-login</v-icon>
            Login
          </v-btn>
        </v-card-actions>
        <v-alert
          class="ma-2"
          type="error"
          :value="isLoginError"
          dense
          outlined
          dismissible
          transition="scale-transition"
        >
          {{ loginErrorMessage }}
        </v-alert>
      </v-card>
    </v-form>
  </div>
</template>

<script>
import axios from 'axios';
import { mask } from 'vue-the-mask';
import ExternalLink from '@/components/ExternalLink';

export default {
  name: 'Login',
  components: {
    ExternalLink,
  },
  directives: { mask },
  data: () => ({
    isLoginError: false,
    loginErrorMessage: '',
    googleRecaptchaApiKey: process.env.VUE_APP_GOOGLE_RECAPTCHA_KEY,
    googleRecaptchaApiUrl: process.env.VUE_APP_GOOGLE_RECAPTCHA_ENDPOINT,
    CID: '',
    dateOfBirth: '',
    birthDateRules: [
      (v) =>
        v.length == 10 || 'Please enter a complete date in mm/dd/yyyy format.',
      (v) =>
        /(0[1-9]|1[012])[/](0[1-9]|[12][0-9]|3[01])[/](19|20)\d\d/.test(v) ||
        'Must be a valid date in the format of mm/dd/yyyy.',
    ],
    valid: false,
    loading: false,
  }),
  computed: {
    formattedDateOfBirth() {
      const dateParts = this.dateOfBirth.split('/');
      const monthPart = dateParts[0];
      const dayPart = dateParts[1];
      const yearPart = dateParts[2];
      return `${yearPart}-${monthPart}-${dayPart}`;
    },
    isValidCIDNumber() {
      return (
        (this.CID && this.CID.length == 9) || 'DMV ID number must be nine digits.'
      );
    },
    isValidCID() {
      let data = this.CID;
      let isValid = false;
      let regex = /^[0-9]{9}$/;
      if (
        regex.test(data) &&
        data.length == 9 &&
        data !== '000000000' &&
        data !== '555555555' &&
        data !== '999999999'
      ) {
        let sum =
          parseInt(data.charAt(0, 1)) * 4 +
          parseInt(data.charAt(1, 1)) * 3 +
          parseInt(data.charAt(2, 1)) * 2 +
          parseInt(data.charAt(3, 1)) * 6 +
          parseInt(data.charAt(4, 1)) * 5 +
          parseInt(data.charAt(5, 1)) * 4 +
          parseInt(data.charAt(6, 1)) * 3 +
          parseInt(data.charAt(7, 1)) * 2;
        sum = sum % 10;
        isValid = sum == parseInt(data.charAt(8, 1));
      }
      return isValid ? true : 'Invalid New York State DMV ID number';
    },
  },
  mounted() {
    //next line places reCAPTCHA callback in global scope (outside of Vue) for Google reCAPTCHA API callback
    window.submitLoginForm = this.submitLoginForm;

    // console.log('test');

    let recaptchaScript = document.createElement('script');
    recaptchaScript.setAttribute('src', this.googleRecaptchaApiUrl);
    document.head.appendChild(recaptchaScript);
  },
  methods: {
    async loginWithoutApi() {
      //used for testing mockup
      this.$emit('navigateTo', 'TestResults', []);
    },
    login() {
      if (!this.$refs.form.validate()) return;
      // eslint-disable-next-line no-undef
      grecaptcha.enterprise.execute();
    },
    async submitLoginForm(reCAPTCHAtoken) {
      // console.log(reCAPTCHAtoken);
      this.loading = true;
      this.isLoginError = false;
      try {
        const response = await axios.post('auth', {
          reCAPTCHAtoken: reCAPTCHAtoken,
          CID: this.CID,
          DoB: this.formattedDateOfBirth,
        });

        //auth succeeded
        this.$emit('navigateTo', 'TestResults', response.data);

      } catch (error) {
        //auth failed
        this.isLoginError = true;
        if (!error.response) {
          //You'll get here if the Azure function does not respond
          this.loginErrorMessage = 'Communication error, please try again.';
        } else {
          //Else log the error message/authentication issue returned from web service
          this.loginErrorMessage = error.response.data.message;
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.centerContainer {
  margin: auto;
  width: 95%;
}
</style>
